import { useEffect, useRef, useState } from "react";
import { RecaptchaVerifier, signInWithPhoneNumber, signOut } from "firebase/auth";
import { toast } from "sonner";
import { Smartphone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { firebaseAuth } from "@/lib/firebase";

const FB_ERRORS = {
  "auth/invalid-phone-number": "Please check the mobile number.",
  "auth/too-many-requests": "Too many tries. Please wait a while and try again.",
  "auth/quota-exceeded": "Phone login is busy today. Please sign in with Google or try tomorrow.",
  "auth/invalid-verification-code": "Wrong code. Please check the SMS and try again.",
  "auth/code-expired": "The code has expired. Please ask for a new one.",
  "auth/billing-not-enabled": "Phone login is not active yet. Please sign in with Google for now.",
  "auth/operation-not-allowed": "Phone login is not active yet. Please sign in with Google for now.",
};
const fbMsg = (e) => FB_ERRORS[e?.code] || `Could not send the code. Please try again.${e?.code ? ` (${e.code.replace("auth/", "")})` : ""}`;

const btn = "w-full rounded-full bg-ink py-3 text-sm font-bold text-white disabled:opacity-40";

export const PhoneLogin = ({ onDone }) => {
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const conf = useRef(null);
  const token = useRef(null);
  const verifier = useRef(null);
  useEffect(() => () => { verifier.current?.clear(); }, []);

  const send = async () => {
    setBusy(true);
    try {
      const auth = firebaseAuth();
      if (!verifier.current) verifier.current = new RecaptchaVerifier(auth, "phone-recaptcha", { size: "invisible" });
      conf.current = await signInWithPhoneNumber(auth, `+91${phone}`, verifier.current);
      setStep("code");
    } catch (e) {
      toast.error(fbMsg(e));
      verifier.current?.clear();
      verifier.current = null;
    }
    setBusy(false);
  };
  const login = async (withName = "") => {
    try {
      const { data } = await api.post("/auth/phone", { id_token: token.current, name: withName });
      signOut(firebaseAuth()).catch(() => {});
      onDone(data);
    } catch (e) {
      if (e?.response?.data?.detail === "NAME_REQUIRED") return setStep("name");
      toast.error(errMsg(e, "Sign-in failed. Please try again."));
    }
  };
  const verify = async () => {
    setBusy(true);
    try {
      const res = await conf.current.confirm(code);
      token.current = await res.user.getIdToken();
      await login();
    } catch (e) {
      toast.error(fbMsg(e));
    }
    setBusy(false);
  };
  const finish = async () => { setBusy(true); await login(name.trim()); setBusy(false); };

  return (
    <div className="space-y-2 text-left" data-testid="phone-login">
      <p className="flex items-center gap-1.5 text-xs font-bold uppercase text-slate-500"><Smartphone className="h-3.5 w-3.5" />Mobile number · ਮੋਬਾਈਲ ਨੰਬਰ · मोबाइल नंबर</p>
      {step === "phone" && (
        <>
          <div className="flex items-center gap-2 rounded-xl border border-line bg-white px-3">
            <span className="text-sm font-semibold text-slate-500">+91</span>
            <input inputMode="numeric" maxLength={10} value={phone} placeholder="98XXXXXXXX" onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
              className="min-w-0 flex-1 bg-transparent py-3 font-mono outline-none" data-testid="phone-input" />
          </div>
          <button id="phone-send-btn" disabled={busy || !/^[6-9]\d{9}$/.test(phone)} onClick={send} className={btn} data-testid="phone-send-btn">{busy ? "Sending…" : "Send code by SMS"}</button>
        </>
      )}
      {step === "code" && (
        <>
          <p className="text-xs text-slate-600" data-testid="phone-code-sent">Code sent to +91 {phone}. <button onClick={() => { setStep("phone"); setCode(""); }} className="font-bold text-saffron" data-testid="phone-change-btn">Change</button></p>
          <Input inputMode="numeric" maxLength={6} value={code} placeholder="6-digit code" onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} className="text-center font-mono text-lg tracking-[0.4em]" data-testid="phone-code-input" />
          <button disabled={busy || code.length !== 6} onClick={verify} className={btn} data-testid="phone-verify-btn">{busy ? "Checking…" : "Sign in"}</button>
        </>
      )}
      {step === "name" && (
        <>
          <p className="text-xs text-slate-600">Welcome! What should we call you?</p>
          <Input value={name} placeholder="Your name" onChange={(e) => setName(e.target.value)} data-testid="phone-name-input" />
          <button disabled={busy || name.trim().length < 2} onClick={finish} className={btn} data-testid="phone-name-btn">Continue</button>
        </>
      )}
      <div id="phone-recaptcha" />
    </div>
  );
};
